import React, { memo } from "react";

import { transformDateToLocalDateString } from "helpers";
import { ShowcaseCard, ShowcaseNotice } from "ui/components/Showcase";
import CheckIcon from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/components/VehicleWizardIntro/scenes/CheckIcon";

import { DAMAGE_INTRO_PINS, DAMAGE_INTRO_SAMPLE } from "../consts";
import { DamageSceneProps } from "../types";

const DAMAGE_STATUS_STEPS = [
  { name: "Prijavljena", done: true },
  { name: "U popravku", done: false },
  { name: "Riješena", done: false },
];

/** Korak "Osnovne informacije": kartica štete s oznakama na vozilu, tijek štete i zapis u povijesti. */
const DamageInfoScene: React.FC<DamageSceneProps> = ({ formValues }) => {
  const name = formValues.name || DAMAGE_INTRO_SAMPLE.name;
  const description = formValues.description || DAMAGE_INTRO_SAMPLE.description;
  const date = transformDateToLocalDateString(formValues.damageDate ?? new Date());

  return (
    <>
      <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
        <div className="p-5">
          <div className="relative h-[10rem] rounded-md bg-gradient-to-br from-slate-50 to-slate-100 p-[10px]">
            <img src={DAMAGE_INTRO_SAMPLE.image} alt="" className="w-full h-full object-contain" />
            {DAMAGE_INTRO_PINS.map((pin) => (
              <span key={pin.left} className="absolute flex w-4 h-4 -ml-2 -mt-2" style={{ left: pin.left, top: pin.top }}>
                <span className="absolute inline-flex w-full h-full rounded-full bg-rose-400 opacity-60 motion-safe:animate-ping" />
                <span className="relative inline-flex w-4 h-4 rounded-full border-2 border-white bg-rose-500" />
              </span>
            ))}
          </div>

          <div className="mt-3 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Šteta</div>
              <div className="text-sm font-bold text-slate-800 truncate">{name}</div>
            </div>
            <span className="px-2 py-0.5 rounded-full text-[0.65rem] font-semibold bg-rose-100 text-rose-600 flex-shrink-0">
              Nova
            </span>
          </div>

          <p className="mt-2 text-[0.65rem] leading-relaxed text-slate-500 truncate">{description}</p>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Tijek štete</div>
          <div className="mt-3 space-y-2">
            {DAMAGE_STATUS_STEPS.map((step) => (
              <div key={step.name} className="flex items-center gap-2">
                {step.done ? (
                  <CheckIcon />
                ) : (
                  <span className="w-5 h-5 rounded-full border-2 border-dashed border-slate-300 flex-shrink-0" />
                )}
                <span className={step.done ? "text-[0.7rem] font-semibold text-slate-700" : "text-[0.7rem] text-slate-400"}>
                  {step.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Zapisano u povijesti vozila"
          subtitle={`${date} · vidljivo na detaljima vozila`}
          iconClassName="bg-rose-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#E11D48" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
            </svg>
          }
        />
      </ShowcaseCard>
    </>
  );
};

export default memo(DamageInfoScene);
